import fPath from 'path';
import type { NodePath } from '@babel/traverse';
import * as t from '@babel/types';
import {
  createJsxLocatorAttributes,
  dataAttributeNameToDataSetKey,
  getJSXTagName,
  hasInjectedDataSetAttribute,
  isFragmentElement,
  isSvgChild,
  shouldInjectTag,
} from './jsx-locator';
import type { PluginOptions, SourceLocatorAttributes } from './type';
import { findWorkspaceRoot, isIncludedFile } from './utils';

const PREFIX = 'locator';
const DEFAULT_INCLUDE = ['**/*.{jsx,tsx,js,ts}'];

const createObjectProperty = (key: string, value: string) => {
  const objectKey = t.isValidIdentifier(key) ? t.identifier(key) : t.stringLiteral(key);
  return t.objectProperty(objectKey, t.stringLiteral(value));
};

const createDataSetProperties = (attrs: SourceLocatorAttributes) => {
  return Object.entries(attrs).map(([key, value]) => {
    return createObjectProperty(dataAttributeNameToDataSetKey(key), value);
  });
};

const insertObjectPropertiesBeforeSpread = (expression: t.ObjectExpression, properties: t.ObjectProperty[]) => {
  const spreadIndex = expression.properties.findIndex((property) => t.isSpreadElement(property));

  if (spreadIndex === -1) {
    expression.properties.push(...properties);
  } else {
    expression.properties.splice(spreadIndex, 0, ...properties);
  }
};

const mergeDataSetAttribute = (attribute: t.JSXAttribute, properties: t.ObjectProperty[]) => {
  if (attribute.value && t.isJSXExpressionContainer(attribute.value)) {
    if (t.isObjectExpression(attribute.value.expression)) {
      insertObjectPropertiesBeforeSpread(attribute.value.expression, properties);
      return;
    }

    attribute.value.expression = t.objectExpression([...properties, t.spreadElement(attribute.value.expression)]);
    return;
  }

  attribute.value = t.jsxExpressionContainer(t.objectExpression(properties));
};

export function rnSourceLocator(options: PluginOptions = {}) {
  const { include = DEFAULT_INCLUDE, exclude = [], prefix = PREFIX, enable = true, attributes, filterTag } = options;

  return function sourceLocatorRnPlugin() {
    return {
      name: '@metagptx/source-locator-rn',
      visitor: {
        JSXOpeningElement(path: NodePath<t.JSXOpeningElement>, state: { filename?: string; file?: { code?: string; opts?: { filename?: string } } }) {
          if (!enable) return;

          const filename = state.filename || state.file?.opts?.filename;
          const code = state.file?.code;
          if (!filename || !code) return;

          const relativePathFromCwd = fPath.relative(process.cwd(), filename);
          if (!isIncludedFile(relativePathFromCwd, include, exclude)) return;
          if (hasInjectedDataSetAttribute(path, prefix)) return;
          if (isFragmentElement(path)) return;
          if (isSvgChild(path)) return;

          const tagName = getJSXTagName(path.node.name);
          if (!shouldInjectTag(tagName, filterTag)) return;

          const projectRoot = findWorkspaceRoot(process.cwd());
          const relativePath = fPath.relative(projectRoot, filename);
          const attrResult = createJsxLocatorAttributes({
            code,
            path,
            prefix,
            relativePath,
            attributes,
          });
          if (!attrResult) return;

          const dataSetProperties = createDataSetProperties(attrResult.attrs);
          const dataSetAttribute = path.node.attributes.find((attribute) => {
            return t.isJSXAttribute(attribute) && t.isJSXIdentifier(attribute.name) && attribute.name.name === 'dataSet';
          });

          if (dataSetAttribute && t.isJSXAttribute(dataSetAttribute)) {
            mergeDataSetAttribute(dataSetAttribute, dataSetProperties);
            return;
          }

          const spreadIndex = path.node.attributes.findIndex((attribute) => t.isJSXSpreadAttribute(attribute));
          const newDataSetAttribute = t.jsxAttribute(
            t.jsxIdentifier('dataSet'),
            t.jsxExpressionContainer(t.objectExpression(dataSetProperties))
          );

          if (spreadIndex === -1) {
            path.node.attributes.push(newDataSetAttribute);
          } else {
            path.node.attributes.splice(spreadIndex, 0, newDataSetAttribute);
          }
        },
      },
    };
  };
}
